import { isNone, Maybe } from '@crocodille/common';
import { RefObject, useCallback, useEffect, useRef } from 'react';

/**
 * Create a function able to access css custom properties of an element
 * outside of context of `React`. This is useful fo high frequency updates
 * such as animations which update css.
 *
 * @param element Reference to an html element.
 *
 * @returns Function which can set custom css properties on given element.
 *          The returned function has stable identity.
 */
export function useCustomProperties(element: RefObject<HTMLElement | null>) {
  const properties = useRef<Partial<Record<string, string>>>({});

  const setProperty = useCallback(
    (property: string, value: Maybe<string>) => {
      if (isNone(value)) {
        delete properties.current[property];
      } else {
        properties.current[property] = value;
      }

      element.current?.style.setProperty(`--${property}`, value ?? null);
    },
    [element],
  );

  const currentElement = element.current;

  useEffect(() => {
    for (const [property, value] of Object.entries(properties.current)) {
      currentElement?.style.setProperty(`--${property}`, value ?? null);
    }
  }, [currentElement]);

  return setProperty;
}
